'use client';

import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import styles from './CaseStudy.module.scss';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { gaClick } from '@/src/helpers/gtag';
import { refreshAOS, useAOS } from '@/src/lib/hooks/useAOS';

export type CaseStudyMetric = {
  value: string;
  label: string;
};

export type CaseStudyEvidence = {
  title: string;
  description: string;
};

export type CaseStudyProps = {
  eyebrow?: string;
  title: string;
  badge: 'averiane' | 'founder';
  headlineResult: string;
  organisation?: string;
  projectName?: string;
  sector?: string;
  contextNote?: string;
  problem?: string;
  intervention?: string;
  interventionBullets?: string[];
  interventionClosing?: string;
  workflowSteps?: string[];
  constraints?: string;
  results?: string;
  metricCards?: CaseStudyMetric[];
  metricsSource?: string;
  evidenceCards?: CaseStudyEvidence[];
  contributionTitle?: string;
  contributionText?: string;
  handover?: string;
  duration?: string;
  scale?: string;
  documentation?: string;
  confidentialityNote?: string;
  variant?: 'full' | 'teaser';
  teaserLabel?: string;
  teaserAttribution?: string;
  teaserDescription?: string;
  ctaLabel?: string;
  ctaHref?: string;
  // Wrap a headerless teaser in its own section (e.g. second card of the homepage proof section).
  sectionWrap?: boolean;
  // Section-level header, rendered only in `teaser` variant (e.g. homepage proof section).
  sectionEyebrow?: string;
  sectionTitle?: string;
  sectionIntro?: string;
};

const renderHighlighted = (text: string) => {
  return text
    .split('*')
    .map((part, i) =>
      i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
    );
};

// Render multi-paragraph content: paragraphs are separated by a blank line (\n\n).
const renderParagraphs = (text: string) => {
  return text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph, index) => <p key={index}>{paragraph}</p>);
};

export function CaseStudy({
  eyebrow,
  title,
  badge,
  headlineResult,
  organisation,
  projectName,
  sector,
  contextNote,
  problem,
  intervention,
  interventionBullets = [],
  interventionClosing,
  workflowSteps = [],
  constraints,
  results,
  metricCards = [],
  metricsSource,
  evidenceCards = [],
  contributionTitle,
  contributionText,
  handover,
  duration,
  scale,
  documentation,
  confidentialityNote,
  variant = 'full',
  teaserLabel,
  teaserAttribution,
  teaserDescription,
  ctaLabel,
  ctaHref,
  sectionWrap = false,
  sectionEyebrow,
  sectionTitle,
  sectionIntro,
  lang = 'fr',
}: CaseStudyProps & { lang?: 'fr' | 'en' }) {
  useAOS();

  const isEn = lang === 'en';

  const labels = {
    badgeAveriane: isEn ? 'Averiane mission' : 'Mission Averiane',
    badgeFounder: isEn ? 'Founder-led experience before Averiane' : "Expérience d'un fondateur",
    problem: isEn ? 'Problem' : 'Problème',
    intervention: isEn ? 'Intervention' : 'Intervention',
    workflow: isEn ? 'Operational workflow' : 'Workflow opérationnel',
    constraints: isEn ? 'Constraints' : 'Contraintes',
    results: isEn ? 'Results' : 'Résultats',
    contribution: isEn ? 'Our contribution' : 'Notre contribution',
    handover: isEn ? 'Handover and collaboration' : 'Transfert et collaboration',
    documentation: isEn ? 'Documentation and handover' : 'Documentation et transfert',
    context: isEn ? 'Context' : 'Contexte',
    ctaFallback: isEn ? 'Read the case study' : 'Lire l’étude de cas',
    seeMore: isEn ? 'See more' : 'Voir plus',
    seeLess: isEn ? 'See less' : 'Voir moins',
  };

  const badgeText = badge === 'averiane' ? labels.badgeAveriane : labels.badgeFounder;
  const badgeClass = badge === 'averiane' ? styles.badgeAveriane : styles.badgeFounder;

  const metaItems = [organisation, projectName, sector, duration, scale]
    .map((item) => item?.trim())
    .filter((item): item is string => Boolean(item));

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [heights, setHeights] = useState<{ full: number; collapsed: number } | null>(null);

  useLayoutEffect(() => {
    if (variant !== 'full') {
      return;
    }
    const el = bodyRef.current;
    if (!el) {
      return;
    }
    const full = el.scrollHeight;
    const collapsed = Math.round(Math.min(Math.max(full * 0.22, 320), 600));
    // Only collapse when the content is tall enough for the cut to be meaningful.
    if (collapsed < full - 48) {
      setHeights({ full, collapsed });
      // Collapsing shifts the layout of everything below, while AOS caches
      // element positions: recompute once the new height is applied.
      requestAnimationFrame(() => refreshAOS());
    }
  }, [variant]);

  const collapsible = variant === 'full' && heights !== null;

  if (variant === 'teaser') {
    const hasSectionHeader = sectionEyebrow?.trim() || sectionTitle?.trim() || sectionIntro?.trim();
    const attribution = teaserAttribution?.trim() || badgeText;
    const card = (
      <article className={styles.teaser} data-aos="fade-up" data-aos-duration="700">
        {teaserLabel && <p className={styles.teaserLabel}>{teaserLabel}</p>}
        <p className={`${styles.teaserAttribution} ${badgeClass}`}>{attribution}</p>
        <h3 className={styles.teaserTitle}>{renderHighlighted(title)}</h3>
        {headlineResult?.trim() && <p className={styles.teaserHeadline}>{headlineResult}</p>}
        {teaserDescription?.trim() && (
          <p className={styles.teaserDescription}>{teaserDescription}</p>
        )}
        {ctaHref && (
          <a
            href={ctaHref}
            className={styles.teaserCta}
            onClick={() => gaClick('case_study_teaser_cta', { title, href: ctaHref })}
          >
            <span>{ctaLabel || labels.ctaFallback}</span>
            <ArrowRight size={16} aria-hidden="true" />
          </a>
        )}
      </article>
    );

    if (!hasSectionHeader && !sectionWrap) {
      return card;
    }

    return (
      <section
        className={`${styles.teaserSection}${sectionWrap && !hasSectionHeader ? ` ${styles.continued}` : ''}`}
        aria-label={sectionTitle?.trim() || title}
      >
        <div className={styles.container}>
          {hasSectionHeader && (
            <header
              className={styles.teaserSectionHeader}
              data-aos="fade-up"
              data-aos-duration="700"
            >
              {sectionEyebrow?.trim() && <p className={styles.eyebrow}>{sectionEyebrow}</p>}
              {sectionTitle?.trim() && (
                <h2 className={styles.title}>{renderHighlighted(sectionTitle)}</h2>
              )}
              {sectionIntro?.trim() && <p className={styles.sectionIntro}>{sectionIntro}</p>}
            </header>
          )}
          {card}
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section} aria-labelledby="case-study-title">
      <div className={styles.container}>
        <div className={styles.document} data-aos="fade-up" data-aos-duration="700">
          <div
            ref={bodyRef}
            className={styles.body}
            style={
              collapsible ? { maxHeight: expanded ? heights.full : heights.collapsed } : undefined
            }
          >
            <div className={styles.badgeRow}>
              <span className={`${styles.badge} ${badgeClass}`}>{badgeText}</span>
            </div>

            <header className={styles.header}>
              {eyebrow?.trim() && <p className={styles.eyebrow}>{eyebrow}</p>}
              <h2 id="case-study-title" className={styles.title}>
                {renderHighlighted(title)}
              </h2>
              {headlineResult?.trim() && <p className={styles.headlineResult}>{headlineResult}</p>}
            </header>

            {metaItems.length > 0 && (
              <p className={styles.meta}>
                {metaItems.map((item, index) => (
                  <Fragment key={`${item}-${index}`}>
                    {index > 0 && <span className={styles.metaSeparator} aria-hidden="true" />}
                    <span>{item}</span>
                  </Fragment>
                ))}
              </p>
            )}

            {contextNote?.trim() && (
              <div className={styles.block}>
                <h3 className={styles.blockLabel}>{labels.context}</h3>
                <div className={styles.blockText}>{renderParagraphs(contextNote)}</div>
              </div>
            )}

            {problem?.trim() && (
              <div className={styles.block}>
                <h3 className={styles.blockLabel}>{labels.problem}</h3>
                <div className={styles.blockText}>{renderParagraphs(problem)}</div>
              </div>
            )}

            {(intervention?.trim() ||
              interventionBullets.length > 0 ||
              interventionClosing?.trim()) && (
              <div className={styles.block}>
                <h3 className={styles.blockLabel}>{labels.intervention}</h3>
                {intervention?.trim() && (
                  <div className={styles.blockText}>{renderParagraphs(intervention)}</div>
                )}
                {interventionBullets.length > 0 && (
                  <ul className={styles.bulletList}>
                    {interventionBullets.map((item, index) => (
                      <li key={`${item}-${index}`}>{item}</li>
                    ))}
                  </ul>
                )}
                {interventionClosing?.trim() && (
                  <div className={styles.blockText}>{renderParagraphs(interventionClosing)}</div>
                )}
              </div>
            )}

            {workflowSteps.length > 0 && (
              <div className={styles.block}>
                <h3 className={styles.blockLabel}>{labels.workflow}</h3>
                <ol className={styles.workflow}>
                  {workflowSteps.map((step, index) => (
                    <li key={`${step}-${index}`} className={styles.workflowStep}>
                      <span className={styles.workflowIndex} aria-hidden="true">
                        {index + 1}
                      </span>
                      <span className={styles.workflowLabel}>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {constraints?.trim() && (
              <div className={styles.block}>
                <h3 className={styles.blockLabel}>{labels.constraints}</h3>
                <div className={styles.blockText}>{renderParagraphs(constraints)}</div>
              </div>
            )}

            {results?.trim() && (
              <div className={styles.block}>
                <h3 className={styles.blockLabel}>{labels.results}</h3>
                <div className={styles.blockText}>{renderParagraphs(results)}</div>
              </div>
            )}

            {contributionText?.trim() && (
              <div className={styles.contribution}>
                <h3 className={styles.contributionTitle}>
                  {contributionTitle?.trim() || labels.contribution}
                </h3>
                <p className={styles.contributionText}>{contributionText}</p>
              </div>
            )}

            {metricCards.length > 0 && (
              <div className={styles.metrics}>
                <dl className={styles.metricsGrid}>
                  {metricCards.map((metric, index) => (
                    <div key={`${metric.label}-${index}`} className={styles.metricCard}>
                      <dd className={styles.metricValue}>{metric.value}</dd>
                      <dt className={styles.metricLabel}>{metric.label}</dt>
                    </div>
                  ))}
                </dl>
                {metricsSource?.trim() && <p className={styles.metricsSource}>{metricsSource}</p>}
              </div>
            )}

            {evidenceCards.length > 0 && (
              <ul className={styles.evidenceGrid}>
                {evidenceCards.map((card) => (
                  <li key={card.title} className={styles.evidenceCard}>
                    <h3 className={styles.evidenceTitle}>{card.title}</h3>
                    <p className={styles.evidenceDescription}>{card.description}</p>
                  </li>
                ))}
              </ul>
            )}

            {handover?.trim() && (
              <div className={styles.block}>
                <h3 className={styles.blockLabel}>{labels.handover}</h3>
                <div className={styles.blockText}>{renderParagraphs(handover)}</div>
              </div>
            )}

            {documentation?.trim() && (
              <div className={styles.block}>
                <h3 className={styles.blockLabel}>{labels.documentation}</h3>
                <div className={styles.blockText}>{renderParagraphs(documentation)}</div>
              </div>
            )}

            {confidentialityNote?.trim() && (
              <p className={styles.confidentialityNote}>{confidentialityNote}</p>
            )}
          </div>

          {collapsible && !expanded && <div className={styles.fade} aria-hidden="true" />}

          {collapsible && (
            <div className={styles.toggleRow}>
              <button
                type="button"
                className={styles.toggleButton}
                aria-expanded={expanded}
                onClick={() => {
                  const next = !expanded;
                  setExpanded(next);
                  // The max-height transition (0.7s) shifts the content below:
                  // let it finish before AOS recomputes element positions.
                  window.setTimeout(() => refreshAOS(), 750);
                  gaClick('case_study_toggle', { title, expanded: next });
                }}
              >
                <span>{expanded ? labels.seeLess : labels.seeMore}</span>
                <ChevronDown size={16} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
